/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Flame, ChevronRight, RefreshCw, CheckCircle2 } from 'lucide-react';

/**
 * Governance Hot Spots V1 — read-only weekly tile.
 * -------------------------------------------------
 * Source: GET /api/governance/boundary-violations?days=7&limit=1000
 * No new endpoint, no LLM, no audit writes, no subscriber UI.
 *
 * Ranks violation types by:
 *   score = blocked_count + (2 × override_count)
 *
 * Severity:
 *   concern → score >= 6 OR override >= 2
 *   watch   → score >= 3
 *   info    → otherwise
 *
 * Top 3 cards. Each [View →] calls boundaryRef.current.applyFilter()
 * to deep-link into the existing Boundary Violations section with the
 * violation_type pre-filtered and days=7.
 */

// Static deterministic hint copy (no LLM)
const HINTS = {
  mark_ready_precondition: 'Counsellors are bypassing intake — review onboarding SOP.',
  crm_pipeline_regression: 'Backward CRM moves attempted; mostly blocked correctly.',
  lifecycle_regression: 'Lifecycle rolls back signal a workflow correction need.',
  care_state_manual_override: 'Manual care_state overrides cluster — check whether the engine signal is misaligned.',
  orchestration_task_completion: 'Decision cards being closed without rationale — coach reviewers.',
  protocol_publish_without_decode: 'Protocols are reaching publish before DECODE — sequence the flow earlier.',
  support_ticket_resolution_without_staff_message: 'Tickets resolving with no audit trail — require resolution_note.',
  suggested_action_vs_auto_action_conflict: 'Suggestions accepted on top of recent auto-actions — pause to review timing.',
};

const LABELS = {
  crm_pipeline_regression: 'CRM Pipeline Regression',
  lifecycle_regression: 'Lifecycle Regression',
  mark_ready_precondition: 'Mark-Ready Precondition',
  care_state_manual_override: 'Care State Manual Override',
  orchestration_task_completion: 'Decision Card Completion',
  protocol_publish_without_decode: 'Protocol Publish without DECODE',
  support_ticket_resolution_without_staff_message: 'Ticket Resolved without Staff Message',
  suggested_action_vs_auto_action_conflict: 'Suggestion vs Auto-Action Conflict',
};

const SEV_TONE = {
  concern: 'border-rose-500/30 bg-rose-500/[0.07] text-rose-200',
  watch:   'border-amber-500/30 bg-amber-500/[0.07] text-amber-200',
  info:    'border-cyan-500/25 bg-cyan-500/[0.05] text-cyan-200',
};

const SEV_BADGE = {
  concern: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  watch:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  info:    'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
};

function _severity(score, override) {
  if (score >= 6 || override >= 2) return 'concern';
  if (score >= 3) return 'watch';
  return 'info';
}

export default function GovernanceHotSpotsTile({ boundaryRef }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/governance/boundary-violations`, {
        params: { days: 7, limit: 1000 },
      });
      setData(r.data || { by_violation_type: {}, total: 0 });
    } catch {
      setData({ by_violation_type: {}, total: 0, error: true });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { topN, totals } = useMemo(() => {
    if (!data?.by_violation_type) {
      return { topN: [], totals: { events: 0, blocked: 0, override: 0 } };
    }
    const ranked = Object.entries(data.by_violation_type).map(([type, c]) => {
      const blocked = c.blocked || 0;
      const override = c.override || 0;
      const score = blocked + 2 * override;
      return {
        type, blocked, override, score,
        severity: _severity(score, override),
        label: LABELS[type] || type,
        hint: HINTS[type] || '',
      };
    }).filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score
        || b.override - a.override
        || a.label.localeCompare(b.label))
      .slice(0, 3);

    const totals = Object.values(data.by_violation_type).reduce(
      (acc, c) => ({
        events: acc.events + (c.total || 0),
        blocked: acc.blocked + (c.blocked || 0),
        override: acc.override + (c.override || 0),
      }),
      { events: 0, blocked: 0, override: 0 },
    );
    return { topN: ranked, totals };
  }, [data]);

  const handleView = (type) => {
    if (boundaryRef?.current?.applyFilter) {
      boundaryRef.current.applyFilter({ violation_type: type, days: 7 });
    } else {
      // Fallback: scroll to anchor at minimum.
      document.getElementById('boundary-violations')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="mb-8" data-testid="governance-hot-spots-tile">
      <div className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-900/30 p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mt-0.5">
              <Flame className="w-4 h-4 text-rose-300" />
            </div>
            <div>
              <h2 className="text-base font-medium text-white" data-testid="hot-spots-title">
                Governance Hot Spots
              </h2>
              <p className="text-xs text-white/50 mt-0.5">
                Last 7 days · ranked by <span className="font-mono">blocked + 2×override</span>
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="border-white/15 text-white/70 hover:bg-white/5"
            data-testid="hot-spots-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Totals strip */}
        <div className="flex items-center gap-3 mb-4 text-sm" data-testid="hot-spots-totals">
          <span className="text-white">
            <span className="font-medium tabular-nums" data-testid="hot-spots-total-events">{totals.events}</span>{' '}
            <span className="text-white/50">events</span>
          </span>
          <span className="text-white/30">·</span>
          <span className="text-rose-300">
            <span className="font-medium tabular-nums" data-testid="hot-spots-total-blocked">{totals.blocked}</span>{' '}
            <span className="opacity-70">blocked</span>
          </span>
          <span className="text-white/30">·</span>
          <span className="text-amber-300">
            <span className="font-medium tabular-nums" data-testid="hot-spots-total-overrides">{totals.override}</span>{' '}
            <span className="opacity-70">overrides</span>
          </span>
        </div>

        {/* Empty state */}
        {!loading && topN.length === 0 && (
          <div data-testid="hot-spots-empty"
            className="rounded border border-emerald-500/20 bg-emerald-500/5 px-4 py-5 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm text-emerald-200 font-medium">No governance hot spots this week.</div>
              <div className="text-xs text-emerald-200/70 mt-0.5">
                Zero blocked attempts and zero overrides in the last 7 days.
              </div>
            </div>
          </div>
        )}

        {/* Top 3 cards */}
        {topN.length > 0 && (
          <div className="grid gap-2.5" data-testid="hot-spots-list">
            {topN.map((r, i) => (
              <div
                key={r.type}
                data-testid={`hot-spot-card-${r.type}`}
                className={`rounded border p-3 flex items-start justify-between gap-3 flex-wrap ${SEV_TONE[r.severity]}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-white/50">#{i + 1}</span>
                    <Badge className={`text-[10px] uppercase ${SEV_BADGE[r.severity]}`}
                      data-testid={`hot-spot-severity-${r.type}`}>
                      {r.severity}
                    </Badge>
                    <span className="text-sm font-medium text-white">{r.label}</span>
                  </div>
                  <div className="text-[11px] text-white/60 tabular-nums" data-testid={`hot-spot-counts-${r.type}`}>
                    <span className="text-rose-300">{r.blocked} blocked</span>
                    <span className="text-white/30"> · </span>
                    <span className="text-amber-300">{r.override} override</span>
                    <span className="text-white/30"> · </span>
                    <span className="text-white/80">score {r.score}</span>
                  </div>
                  {r.hint && (
                    <div className="text-xs text-white/70 italic mt-1.5 leading-snug">
                      {r.hint}
                    </div>
                  )}
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => handleView(r.type)}
                  className="border-white/15 text-white/80 hover:bg-white/5 shrink-0"
                  data-testid={`hot-spot-view-${r.type}`}
                >
                  View <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
